(() => {
  if (window.__soldOutCartGuardInstalled) return;
  window.__soldOutCartGuardInstalled = true;

  const ADD_URL_FRAGMENT = '/cart/add';
  const MESSAGE =
    'This item is sold out on our website — available on our official Amazon and Nykaa stores.';

  const soldOutIds = new Set();

  const registerId = (id) => {
    if (id === undefined || id === null) return;
    const key = String(id).trim();
    if (key) soldOutIds.add(key);
  };

  /* ---------- build the registry from rendered data ---------- */

  const scanMarketplaceButtons = () => {
    for (const el of document.querySelectorAll('marketplace-buttons[data-variants]')) {
      let data;
      try {
        data = JSON.parse(el.dataset.variants);
      } catch {
        continue;
      }
      for (const [id, variant] of Object.entries(data ?? {})) {
        // Sold out ONLY when explicitly true (matches the PDP/card logic).
        if (variant?.isSoldOut === true) registerId(id);
      }
    }
  };

  const scanMarkers = () => {
    for (const el of document.querySelectorAll('[data-sold-out-variant-id]')) {
      registerId(el.getAttribute('data-sold-out-variant-id'));
    }
  };

  const scan = () => {
    scanMarketplaceButtons();
    scanMarkers();
  };

  /* ---------- extract variant id(s) from a request body ---------- */

  const collectFromObject = (obj, ids) => {
    if (!obj || typeof obj !== 'object') return;
    if (obj.id !== undefined && obj.id !== null) ids.push(obj.id);
    if (Array.isArray(obj.items)) {
      for (const item of obj.items) {
        if (item && item.id !== undefined && item.id !== null) ids.push(item.id);
      }
    }
  };

  const extractIds = (body) => {
    const ids = [];
    if (!body) return ids;

    try {
      if (typeof body === 'string') {
        try {
          collectFromObject(JSON.parse(body), ids);
        } catch {
          const params = new URLSearchParams(body);
          if (params.get('id')) ids.push(params.get('id'));
        }
      } else if (body instanceof URLSearchParams) {
        if (body.get('id')) ids.push(body.get('id'));
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        for (const [key, value] of body.entries()) {
          if (key === 'id' || /\[id\]$/.test(key)) ids.push(value);
        }
      } else if (typeof body === 'object') {
        collectFromObject(body, ids);
      }
    } catch {
      /* best effort — fall through with whatever we collected */
    }

    return ids.map(String);
  };

  const isAddUrl = (url) => typeof url === 'string' && url.includes(ADD_URL_FRAGMENT);

  const hasSoldOut = (ids) => ids.some((id) => soldOutIds.has(String(id)));

  const notifyBlocked = () => {
    document.dispatchEvent(
      new CustomEvent('theme:cart:sold-out-blocked', { bubbles: true, detail: { message: MESSAGE } })
    );
  };

  /* ---------- tell console / programmatic calls from real user actions ----------
     A genuine add-to-cart fires within a few seconds of a trusted user gesture
     (click / tap / key). A call typed into the console has no such gesture, so we
     treat "no recent trusted gesture" as programmatic and block ONLY those —
     leaving every real on-site interaction completely untouched. */
  let lastGestureAt = 0;
  const GESTURE_WINDOW_MS = 4000;
  for (const type of ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'click']) {
    window.addEventListener(
      type,
      (event) => {
        if (event.isTrusted) lastGestureAt = Date.now();
      },
      true
    );
  }
  const isProgrammatic = () => Date.now() - lastGestureAt > GESTURE_WINDOW_MS;

  /* ---------- patch fetch ---------- */

  const originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    // Traditional function: needs the original `this`/`arguments` passthrough.
    window.fetch = function patchedFetch(input, init) {
      try {
        const url = typeof input === 'string' ? input : input?.url ?? '';
        if (isAddUrl(url) && isProgrammatic()) {
          const body = init?.body ?? (input && typeof input !== 'string' ? input.body : null);
          if (hasSoldOut(extractIds(body))) {
            notifyBlocked();
            return Promise.reject(new Error('Add to cart blocked: variant is sold out.'));
          }
        }
      } catch {
        /* never let the guard break a real request */
      }
      return originalFetch.apply(this, arguments);
    };
  }

  /* ---------- patch XMLHttpRequest (best effort) ---------- */

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function patchedOpen(method, url) {
    this.__soldOutGuardUrl = url;
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function patchedSend(body) {
    try {
      if (
        isAddUrl(this.__soldOutGuardUrl ?? '') &&
        isProgrammatic() &&
        hasSoldOut(extractIds(body))
      ) {
        notifyBlocked();
        throw new Error('Add to cart blocked: variant is sold out.');
      }
    } catch {
      /* fall through to a normal send */
    }
    return originalSend.apply(this, arguments);
  };

  /* ---------- keep the registry fresh ---------- */

  let scanScheduled = false;
  const scheduleScan = () => {
    if (scanScheduled) return;
    scanScheduled = true;
    requestAnimationFrame(() => {
      scanScheduled = false;
      scan();
    });
  };

  const init = () => {
    scan();
    new MutationObserver(scheduleScan).observe(document.body, {
      childList: true,
      subtree: true,
    });
    document.addEventListener('eurus:cart:items-changed', scheduleScan);
    document.addEventListener('shopify:section:load', scheduleScan);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
